<template>
  <div
    class="modal fade"
    id="advancedOptionsModal"
    tabindex="-1"
    aria-hidden="true"
    ref="modalRef"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
    >
      <div class="modal-content advanced-modal">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t("qbank.new_test.advanced_options") }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Difficulty Levels Section -->
          <div class="options-section">
            <h6 class="section-title">
              <span class="section-icon">📊</span>
              {{ $t("qbank.new_test.difficulty_levels") }}
            </h6>
            <div class="chips-container">
              <div
                v-for="level in difficultyLevels"
                :key="level.key"
                class="filter-chip"
                :class="{ selected: localDifficultyLevels.has(level.key) }"
                @click="toggleDifficulty(level.key)"
              >
                <span class="chip-label">{{
                  getDifficultyName(level.key)
                }}</span>
                <span class="chip-count">({{ level.count }})</span>
              </div>
            </div>
          </div>

          <!-- Question Types Section -->
          <div class="options-section">
            <h6 class="section-title">
              <span class="section-icon">📝</span>
              {{ $t("qbank.new_test.question_types") }}
            </h6>
            <div class="chips-container">
              <div
                v-for="qtype in questionTypes"
                :key="qtype.key"
                class="filter-chip"
                :class="{ selected: localQuestionTypes.has(qtype.key) }"
                @click="toggleQuestionType(qtype.key)"
              >
                <span class="chip-label">{{
                  getQuestionTypeName(qtype.key)
                }}</span>
                <span class="chip-count">({{ qtype.count }})</span>
              </div>
            </div>
            <p v-if="questionTypes.length === 0" class="empty-hint">
              {{ $t("qbank.new_test.no_question_types") }}
            </p>
          </div>

          <!-- Sources Section -->
          <div class="options-section">
            <h6 class="section-title">
              <span class="section-icon">📖</span>
              {{ $t("qbank.new_test.sources") }}
            </h6>
            <div class="chips-container">
              <div
                v-for="source in sources"
                :key="source.id"
                class="filter-chip"
                :class="{ selected: localSources.has(source.id) }"
                @click="toggleSource(source.id)"
              >
                <span class="chip-label">{{ source.name }}</span>
                <span class="chip-count">({{ source.count }})</span>
              </div>
            </div>
            <p v-if="sources.length === 0" class="empty-hint">
              {{ $t("qbank.new_test.no_sources") }}
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-clear" @click="clearAll">
            {{ $t("qbank.new_test.clear_all") }}
          </button>
          <button
            type="button"
            class="btn btn-confirm"
            data-bs-dismiss="modal"
            @click="saveOptions"
          >
            {{ $t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdvancedOptionsModal",
  props: {
    difficultyLevels: {
      type: Array,
      default: () => [],
    },
    sources: {
      type: Array,
      default: () => [],
    },
    questionTypes: {
      type: Array,
      default: () => [],
    },
    selectedDifficultyLevels: {
      type: Array,
      default: () => [],
    },
    selectedSources: {
      type: Array,
      default: () => [],
    },
    selectedQuestionTypes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["save"],
  data() {
    return {
      localDifficultyLevels: new Set(),
      localSources: new Set(),
      localQuestionTypes: new Set(),
      modalInstance: null,
    };
  },
  watch: {
    selectedDifficultyLevels: {
      immediate: true,
      handler(newVal) {
        this.localDifficultyLevels = new Set(newVal);
      },
    },
    selectedSources: {
      immediate: true,
      handler(newVal) {
        this.localSources = new Set(newVal);
      },
    },
    selectedQuestionTypes: {
      immediate: true,
      handler(newVal) {
        this.localQuestionTypes = new Set(newVal);
      },
    },
  },
  methods: {
    show() {
      this.localDifficultyLevels = new Set(this.selectedDifficultyLevels);
      this.localSources = new Set(this.selectedSources);
      this.localQuestionTypes = new Set(this.selectedQuestionTypes);

      if (!this.modalInstance) {
        this.modalInstance = new bootstrap.Modal(this.$refs.modalRef);
      }
      this.modalInstance.show();
    },
    getDifficultyName(key) {
      const names = {
        easy: this.$t("qbank.difficulty.easy"),
        medium: this.$t("qbank.difficulty.medium"),
        hard: this.$t("qbank.difficulty.hard"),
        very_hard: this.$t("qbank.difficulty.very_hard"),
      };
      return names[key] || key;
    },
    toggleDifficulty(key) {
      if (this.localDifficultyLevels.has(key)) {
        this.localDifficultyLevels.delete(key);
      } else {
        this.localDifficultyLevels.add(key);
      }
      this.localDifficultyLevels = new Set(this.localDifficultyLevels);
    },
    toggleSource(id) {
      if (this.localSources.has(id)) {
        this.localSources.delete(id);
      } else {
        this.localSources.add(id);
      }
      this.localSources = new Set(this.localSources);
    },
    toggleQuestionType(key) {
      if (this.localQuestionTypes.has(key)) {
        this.localQuestionTypes.delete(key);
      } else {
        this.localQuestionTypes.add(key);
      }
      this.localQuestionTypes = new Set(this.localQuestionTypes);
    },
    getQuestionTypeName(key) {
      const names = {
        multiple_choice: this.$t("qbank.question_type.multiple_choice"),
        true_false: this.$t("qbank.question_type.true_false"),
        written: this.$t("qbank.question_type.written"),
        matching: this.$t("qbank.question_type.matching"),
      };
      return names[key] || key;
    },
    clearAll() {
      this.localDifficultyLevels = new Set();
      this.localSources = new Set();
      this.localQuestionTypes = new Set();
    },
    saveOptions() {
      this.$emit("save", {
        difficultyLevels: [...this.localDifficultyLevels],
        sources: [...this.localSources],
        questionTypes: [...this.localQuestionTypes],
      });
    },
  },
};
</script>

<style scoped>
.advanced-modal {
  border-radius: 20px;
  border: none;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 20px 24px;
}

.modal-title {
  font-weight: 700;
  font-size: 1.1rem;
}

.modal-header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.modal-header .btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Scrollbar Styling - Green Theme */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(0, 196, 68, 0.08);
  border-radius: 4px;
  margin: 4px 0;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgb(0, 179, 62);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 179, 62, 0.8);
  background-clip: padding-box;
}

.modal-body::-webkit-scrollbar-thumb:active {
  background: rgba(0, 179, 62, 0.9);
  background-clip: padding-box;
}

/* Firefox scrollbar */
.modal-body {
  scrollbar-width: thin;
  scrollbar-color: rgb(0, 179, 62) rgba(0, 196, 68, 0.08);
}

.options-section {
  margin-bottom: 28px;
}

.options-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  font-size: 1.1rem;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f1f5f9;
  border: 2px solid #e2e8f0;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-chip:hover {
  border-color: #00c744;
}

.filter-chip.selected {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-color: #00c744;
  color: white;
}

.chip-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.chip-count {
  font-size: 0.8rem;
  opacity: 0.8;
}

.empty-hint {
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0;
}

.modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
  gap: 12px;
}

.btn-clear {
  flex: 1;
  padding: 14px 24px;
  background: #f1f5f9;
  border: none;
  border-radius: 12px;
  color: #64748b;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.btn-clear:hover {
  background: #e2e8f0;
  color: #475569;
}

.btn-confirm {
  flex: 2;
  padding: 14px 24px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.3);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 199, 68, 0.4);
  color: white;
}

/* Dark Mode */
[data-bs-theme="dark"] .advanced-modal,
.dark .advanced-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .section-title,
.dark .section-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .filter-chip,
.dark .filter-chip {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .filter-chip:hover,
.dark .filter-chip:hover {
  border-color: #00c744;
}

[data-bs-theme="dark"] .filter-chip.selected,
.dark .filter-chip.selected {
  color: white;
}

[data-bs-theme="dark"] .modal-footer,
.dark .modal-footer {
  background: #1e293b;
}

[data-bs-theme="dark"] .btn-clear,
.dark .btn-clear {
  background: #334155;
  color: #94a3b8;
}

[data-bs-theme="dark"] .btn-clear:hover,
.dark .btn-clear:hover {
  background: #475569;
  color: #e2e8f0;
}
</style>
